import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2 } from 'lucide-react';
import { useDebounce } from '@/hooks/useDebounce';
import { getAccounts } from '../api';
import { useReassignEntity } from '../hooks/useReassign';

type EntityType = 'invoice' | 'quote' | 'contract' | 'sensor' | 'transmitter';

interface ReassignDialogProps {
  entityType: EntityType;
  entityId: string;
  entityLabel: string;
  currentAccountId: string;
  currentAccountName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function ReassignDialog({
  entityType,
  entityId,
  entityLabel,
  currentAccountId,
  currentAccountName,
  open,
  onOpenChange,
}: ReassignDialogProps) {
  const [search, setSearch] = useState('');
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
  const [selectedAccountName, setSelectedAccountName] = useState<string | null>(null);
  const debouncedSearch = useDebounce(search, 300);

  const reassignMutation = useReassignEntity(entityType, currentAccountId);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      setSearch('');
      setSelectedAccountId(null);
      setSelectedAccountName(null);
    }
  }, [open]);

  const { data: accountsData, isLoading: isSearching } = useQuery({
    queryKey: ['accounts-search', debouncedSearch],
    queryFn: () => getAccounts({ search: debouncedSearch, pageLength: 10 }),
    enabled: open && debouncedSearch.length >= 2,
  });

  const accounts = (accountsData?.accounts || []).filter(
    a => a.id !== currentAccountId && a.mongo_organisation_id !== currentAccountId,
  );

  const handleConfirm = async () => {
    if (!selectedAccountId) return;
    try {
      await reassignMutation.mutateAsync({ entityId, targetAccountId: selectedAccountId });
      onOpenChange(false);
    } catch {
      // Error toast is handled by onError in the mutation hook
    }
  };

  const handleSelect = (id: string, name: string) => {
    setSelectedAccountId(id);
    setSelectedAccountName(name);
    setSearch('');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reassign {entityType}</DialogTitle>
          <DialogDescription>
            {currentAccountName ? (
              <>Move <span className="font-medium">{entityLabel}</span> from{' '}
              <span className="font-medium">{currentAccountName}</span> to a different account.</>
            ) : (
              <>Assign <span className="font-medium">{entityLabel}</span> to an account.</>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {selectedAccountId ? (
            <div className="flex items-center justify-between rounded-md border p-3">
              <span className="text-sm font-medium">{selectedAccountName}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSelectedAccountId(null);
                  setSelectedAccountName(null);
                }}
              >
                Change
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              <Input
                placeholder="Search accounts by name or number..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoFocus
              />
              {debouncedSearch.length >= 2 && (
                <div className="max-h-48 overflow-y-auto rounded-md border">
                  {isSearching ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    </div>
                  ) : accounts.length === 0 ? (
                    <div className="py-4 text-center text-sm text-muted-foreground">
                      No accounts found
                    </div>
                  ) : (
                    accounts.map((account) => (
                      <button
                        key={account.id}
                        className="w-full px-3 py-2 text-left text-sm hover:bg-muted cursor-pointer"
                        onClick={() => handleSelect(account.id, account.name)}
                      >
                        <span className="font-medium">{account.account_number}</span>
                        <span className="mx-2 text-muted-foreground">-</span>
                        <span>{account.name}</span>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!selectedAccountId || reassignMutation.isPending}
          >
            {reassignMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Reassign
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
